import { Button } from 'designSystem/components/Button'
import { KNOB_HELP } from 'features/timeSeries/experiment/knobCopy'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import { SizedPlot } from 'features/timeSeries/experiment/SizedPlot'
import { TagLabel } from 'features/timeSeries/experiment/TagLabel'
import {
  CrossCorrelation,
  IntervalUnit,
} from 'features/timeSeries/experiment/types'
import { Layout, PlotData } from 'plotly.js'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const FG = CantileverTheme.colors.grayscale['100']
const GRID = CantileverTheme.colors.grayscale['600']

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.forecast.background2};
`

const Title = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  align-items: flex-end;
`

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  min-width: 7rem;
`

const Input = styled.input`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
`

const Select = styled.select`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const Chip = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  font-size: 0.72rem;
`

const PhaseGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const PhaseRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.35rem;
  align-items: center;
  font-size: 0.72rem;
`

const short = (id: string) => id.split('-').slice(1).join('-') || id

/** Human-readable duration for N periods × interval. */
export const formatShiftDuration = (
  periods: number,
  interval: number,
  unit: IntervalUnit
): string => {
  const abs = Math.abs(periods)
  let seconds = abs * interval
  if (unit === 'minutes') seconds *= 60
  else if (unit === 'hours') seconds *= 3600
  if (seconds >= 3600) {
    const h = seconds / 3600
    return `${abs} × ${interval}${unit === 'hours' ? 'h' : unit === 'minutes' ? 'min' : 's'} = ${
      Number.isInteger(h) ? h : h.toFixed(1)
    }h`
  }
  if (seconds >= 60) {
    const m = seconds / 60
    return `${abs} × ${interval}${unit === 'minutes' ? 'min' : 's'} = ${
      Number.isInteger(m) ? m : m.toFixed(1)
    }min`
  }
  return `${abs} × ${interval}s = ${seconds}s`
}

export interface CorrelationCardProps {
  correlation: CrossCorrelation | null | undefined
  drivers: string[]
  phaseShifts: Record<string, number>
  corrShiftMax: number
  corrShiftStep: number
  interval: number
  intervalUnit: IntervalUnit
  onCorrShiftMaxChange: (n: number) => void
  onCorrShiftStepChange: (n: number) => void
  onPhaseShiftsChange: (shifts: Record<string, number>) => void
}

export const CorrelationCard: FC<CorrelationCardProps> = ({
  correlation,
  drivers,
  phaseShifts,
  corrShiftMax,
  corrShiftStep,
  interval,
  intervalUnit,
  onCorrShiftMaxChange,
  onCorrShiftStepChange,
  onPhaseShiftsChange,
}) => {
  const corrChart = useMemo(() => {
    const corr = correlation
    if (!corr?.shifts?.length) return null
    const data: Partial<PlotData>[] = Object.entries(corr.by_driver).map(
      ([driver, info], i) => ({
        x: corr.shifts,
        y: info.correlation,
        type: 'scatter',
        mode: 'lines+markers',
        name: short(driver),
        line: {
          width: 1.2,
          color: ['#22d3ee', '#f97316', '#a78bfa', '#34d399'][i % 4],
        },
      })
    )
    const layout: Partial<Layout> = {
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      margin: { l: 48, r: 12, t: 28, b: 36 },
      legend: { font: { color: FG, size: 10 } },
      title: {
        text: 'Cross-correlation vs phase shift (periods)',
        font: { color: FG, size: 12 },
        x: 0,
      },
      xaxis: {
        title: {
          text: 'shift (positive = driver leads)',
          font: { color: FG, size: 10 },
        },
        tickfont: { color: FG, size: 10 },
        gridcolor: GRID,
      },
      yaxis: {
        title: { text: 'corr', font: { color: FG, size: 10 } },
        tickfont: { color: FG, size: 10 },
        gridcolor: GRID,
      },
    }
    return { data, layout }
  }, [correlation])

  const applyBestAll = () => {
    if (!correlation) return
    const next: Record<string, number> = { ...phaseShifts }
    for (const [driver, info] of Object.entries(correlation.by_driver)) {
      if (info.best_shift !== null && info.best_shift !== undefined)
        next[driver] = info.best_shift
    }
    onPhaseShiftsChange(next)
  }

  const applyBestOne = (driver: string) => {
    const info = correlation?.by_driver?.[driver]
    if (info?.best_shift === null || info?.best_shift === undefined) return
    onPhaseShiftsChange({
      ...phaseShifts,
      [driver]: info.best_shift,
    })
  }

  const setShift = (tag: string, value: number) => {
    onPhaseShiftsChange({
      ...phaseShifts,
      [tag]: value,
    })
  }

  return (
    <Card>
      <Title>
        <KnobHelp
          label='Correlation & phase shifts'
          help={KNOB_HELP.phaseShifts}
        />
      </Title>
      <Hint>
        Positive shift = driver leads the target. Scan window:{' '}
        {formatShiftDuration(corrShiftMax, interval, intervalUnit)} each side
        (step {corrShiftStep}).
      </Hint>

      <Row>
        <Field>
          <KnobHelp label='Max shift (periods)' help={KNOB_HELP.corrShiftMax} />
          <Input
            type='number'
            min={1}
            max={500}
            value={corrShiftMax}
            onChange={(e) =>
              onCorrShiftMaxChange(Math.max(1, Number(e.target.value) || 36))
            }
          />
        </Field>
        <Field>
          <KnobHelp label='Step' help={KNOB_HELP.corrShiftStep} />
          <Select
            value={corrShiftStep}
            onChange={(e) =>
              onCorrShiftStepChange(Number(e.target.value) || 1)
            }>
            {[1, 2, 3, 5, 10, 15, 20].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </Field>
        <Button variant='outline' onClick={applyBestAll}>
          Apply best shifts (all)
        </Button>
        <KnobHelp label='About best shifts' help={KNOB_HELP.applyBestShifts} />
      </Row>

      {drivers.length > 0 && (
        <ChipRow>
          {drivers.map((driver) => {
            const info = correlation?.by_driver?.[driver]
            const applied = phaseShifts[driver] ?? 0
            return (
              <Chip key={driver}>
                <TagLabel tagId={driver} />
                <span>
                  best {info?.best_shift ?? '—'}
                  {info?.best_correlation !== null &&
                  info?.best_correlation !== undefined
                    ? ` (r=${info.best_correlation.toFixed(2)})`
                    : ''}
                </span>
                <span>· applied {applied}</span>
                <Button
                  variant='outline'
                  disabled={
                    info?.best_shift === null || info?.best_shift === undefined
                  }
                  onClick={() => applyBestOne(driver)}>
                  Apply
                </Button>
              </Chip>
            )
          })}
        </ChipRow>
      )}

      {corrChart ? (
        <SizedPlot
          key={`corr-${Object.keys(correlation?.by_driver || {}).length}-${corrShiftMax}-${corrShiftStep}`}
          $heightRem={14}
          data={corrChart.data}
          layout={corrChart.layout}
        />
      ) : (
        <Hint>Correlation chart loads after preview has drivers + target.</Hint>
      )}

      <PhaseGrid>
        <Hint>Manual phase shift (periods) per driver:</Hint>
        {drivers.map((tag) => (
          <PhaseRow key={tag}>
            <TagLabel tagId={tag} />
            <Input
              type='number'
              style={{ width: '4.5rem' }}
              value={phaseShifts[tag] ?? 0}
              onChange={(e) => setShift(tag, Number(e.target.value) || 0)}
            />
            <span>
              {phaseShifts[tag]
                ? formatShiftDuration(
                    phaseShifts[tag] ?? 0,
                    interval,
                    intervalUnit
                  )
                : '—'}
            </span>
          </PhaseRow>
        ))}
      </PhaseGrid>
    </Card>
  )
}
